import * as csiImage from "csi/image/_base";
import printUtil from "csi/printUtil";
import reflection from "csi/reflection";
import { getSearch, runSearch } from "csi/service/dataStoreService";
import { resolveNode, resolveNodes } from "csi/service/nodeService";
import StoreCache from "csi/store/Cache";
import { C8Node, cnl, ICache } from "csi/typings";
import { pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import * as _ from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { createContext, useContext } from "react";

export const LEFT = "left";
export const SLIDE_WIDTH = 160;

function _getSplit(page: C8Node, image: C8Node, layoutSize: [number, number], notesLeft: boolean): number {
	const { SplitPct } = page;

	// handle edge cases where there is either no image or no notes
	if (SplitPct < 0.02) {
		return 0;
	} else if (SplitPct > 0.98) {
		return 1;
	} else if (!image) {
		return SplitPct;
	}

	const { Height, Width } = image;
	const [w, h] = layoutSize;

	// base true split percentage on image size rather than stored SplitPct (just a bit more accurate)
	const split = Width * h / (Height * w);
	return notesLeft ? split : (1 / split);
};

export default class PresentationPopupStore {
	readonly cache: ICache;
	windowHeight: number;
	@observable.ref private splitPcts: number[];
	@observable.ref comments: C8Node[] = [];
	@observable.ref private _imageURLs: cnl[] = [];
	@observable.ref private _currentLayout: C8Node;
	@observable.ref private _layouts: C8Node[] = [];
	@observable private _layoutIndex: number = 0;
	@observable.ref private _thumbnails: string[];
	@observable private _selectedImage = 0;
	@observable private _showHeader = false;
	@observable showSlides = false;
	@observable showLayouts = false;
	@observable showRightArrow = false;
	@observable showLeftArrow = false;
	@observable private _notesLeft = false;
	@observable private _started = false;
	@observable private _fullScreen = false;

	get isFullScreen(): boolean {
		return this._fullScreen;
	}

	get notesLeft(): boolean {
		return this._notesLeft;
	}

	get layout(): C8Node {
		return this._currentLayout;
	}

	get numberOfLayouts(): number {
		return this._layouts.length;
	}

	get numberOfSlides(): number {
		return this._thumbnails?.length ?? 0;
	}

	get showHeader(): boolean {
		return this._showHeader || this.showLayouts;
	}

	@computed get splitPct(): number {
		return this.splitPcts?.[this._selectedImage] || 0;
	}

	get started(): boolean {
		return this._started;
	}

	@computed get nextLayout(): C8Node | null {
		const { _layoutIndex, _layouts } = this;
		return _layouts[_layoutIndex + 1];
	}

	@computed get prevLayout(): C8Node | null {
		const { _layoutIndex, _layouts } = this;
		return _layouts[_layoutIndex - 1];
	}

	@computed get nextLayoutName(): string | null {
		return this.nextLayout?.Name;
	}

	@computed get prevLayoutName(): string {
		return this.prevLayout?.Name;
	}

	get canNextImage(): boolean {
		return (this._selectedImage + 1) < this.numberOfImages;
	}

	get canPrevImage(): boolean {
		return this._selectedImage > 0;
	}

	get images(): cnl[] {
		return this._imageURLs;
	}

	get thumbnails(): string[] {
		return this._thumbnails;
	}

	get allLayouts(): C8Node[] {
		return this._layouts;
	}

	get layoutIndex(): number {
		return this._layoutIndex;
	}

	@computed get layoutAspectRatio(): number {
		const [w, h] = this.layoutSize;
		return h / w;
	}

	@computed get layoutSize(): [number, number] {
		const { _currentLayout: { PaperOrientation, PaperSize } } = this;
		const { h, w } = printUtil.getPageSizeForSettings(xml.enumGetText(PaperSize), xml.enumGetText(PaperOrientation));
		return [w, h];
	}

	getSlideDimensions = (index: number): any => {
		const notesLeft = this._notesLeft;
		const splitPct = this.splitPcts[index];
		const totalWidth = SLIDE_WIDTH;
		const totalHeight = totalWidth * this.layoutAspectRatio;;
		const thumbWidth = notesLeft ? totalWidth * splitPct : totalWidth;
		const thumbHeight = !notesLeft ? totalHeight * splitPct : totalHeight;
		const boxWidth = notesLeft ? totalWidth * (1 - splitPct) : thumbWidth;
		const boxHeight = !notesLeft ? totalHeight * (1 - splitPct) : thumbHeight;

		return { totalHeight, thumbWidth, thumbHeight, boxWidth, boxHeight };
	};

	@computed get numberOfImages(): number {
		return this._imageURLs.length;
	}

	@computed get selectedImage(): string {
		return this._imageURLs[this._selectedImage];
	}

	get selectedImageIndex(): number {
		return this._selectedImage;
	}

	getSlideSplitPct = (index: number): number => {
		return this.splitPcts[index];
	};

	getSlideSrc = (index: number): string => {
		return this._thumbnails[index];
	};

	@action setIsFullScreen = (fullScreen: boolean): void => {
		this._fullScreen = fullScreen;
	};

	@action start = (): void => {
		this._started = true;
		document.documentElement.requestFullscreen();
		this.setIsFullScreen(true);
	};

	@action toggleShowLayouts = (): void => {
		this.showLayouts = !this.showLayouts;
	};

	@action hideLayouts = (): void => {
		this.showLayouts = false;
	};

	@action nextImage = (): void => {
		const { canNextImage, nextLayout, numberOfImages, selectedImageIndex } = this;

		if (!canNextImage && nextLayout) {
			this.switchLayout(nextLayout);
		} else {
			this.switchPage(Math.min(numberOfImages - 1, selectedImageIndex + 1));
		}
	};

	@action prevImage = (): void => {
		const { canPrevImage, prevLayout, selectedImageIndex } = this;

		if (!canPrevImage && prevLayout) {
			this.switchLayout(prevLayout, true);
		} else {
			this.switchPage(Math.max(0, selectedImageIndex - 1));
		}
	};

	@action hideHeader = (): void => {
		this._showHeader = false;
	};

	@action toggleLeftArrow = (show: boolean): void => {
		this.showLeftArrow = show;
	};

	@action toggleRightArrow = (show: boolean): void => {
		this.showRightArrow = show;
	};

	@action makeHeaderVisible = (): void => {
		this._showHeader = true;
	};

	@action toggleSlides = (): void => {
		this.showSlides = !this.showSlides;
	};

	@action switchPage = async (index: number): Promise<void> => {
		const { cache, _currentLayout } = this;
		const pageUrl = _currentLayout.Pages[index];

		this.comments = [];
		this._selectedImage = index;

		const search = getSearch("Comment.DataSheetPage", pageUrl);
		search.addDependencyPaths([pathCreate("Author")]);
		const response = await runSearch(search, cache);

		runInAction(() => {
			this.comments = response.getResultNodes();
		});
	};

	@action _switchHelper = (results: C8Node[], selectLastPage?: boolean): void => {
		const { cache, layoutSize, _notesLeft } = this;
		const newImages = [];
		const newThumbnails = [];
		const newSplitPcts = [];

		results.forEach((node) => {
			const snapshotNode = cache.get(node.Snapshot);
			newImages.push(csiImage.getImageSrc(snapshotNode, null, reflection.ATTR_VIEWABLE));
			newThumbnails.push(csiImage.getImageSrc(snapshotNode, null, reflection.ATTR_THUMBNAIL));
			newSplitPcts.push(_getSplit(node, snapshotNode, layoutSize, _notesLeft));
		});

		this._imageURLs = newImages;
		this._thumbnails = newThumbnails;
		this.splitPcts = newSplitPcts;
		this.switchPage(selectLastPage ? results.length - 1 : 0);
	};

	@action switchLayout = async (layoutNode: C8Node, selectLastPage?: boolean): Promise<void> => {
		const cache = this.cache;

		this._currentLayout = layoutNode;
		this._notesLeft = (xml.enumGetText(layoutNode.NotesOrientation) == LEFT);
		this._layoutIndex = _.findIndex(this._layouts, (item) => item.$URL === layoutNode.$URL);

		const search = getSearch("DataSheetPage.CanvasContainer", xml.nodeGetURL(layoutNode));
		search.addDependencyPaths([pathCreate("Snapshot")]);

		await runSearch(search, cache, (nodes) => {
			this._switchHelper(nodes, selectLastPage);
		});
	};

	@action setLayouts = (layouts: C8Node[]): void => {
		this._layouts = layouts;
	};

	@action setLayoutIndex = (index: number): void => {
		this._layoutIndex = index;
	};

	loadLayouts = async (layoutNode: C8Node, cache: ICache): Promise<void> => {
		const presentationNode = await resolveNode(layoutNode.__Parent__, cache);
		const layoutUrls = presentationNode.Layouts;
		const layouts = await resolveNodes(layoutUrls, cache);

		const nonEmptyLayouts = layouts.filter((layout) => !!layout.Pages.length);
		this.setLayouts(nonEmptyLayouts);
		this._layouts.forEach((item, i) => {
			if (item.$URL == this._currentLayout.$URL) {
				this.setLayoutIndex(i);
			}
		});
	};

	onClickFullscreen = (): void => {
		if (this.isFullScreen) {
			document.exitFullscreen();
		  } else {
			document.documentElement.requestFullscreen();
		  }
		  this.setIsFullScreen(!this.isFullScreen);
	};

	handleFullScreenChange = (): void => {
		this.setIsFullScreen(!!document.fullscreenElement);
	};

	constructor(layoutNode: C8Node, cache?: ICache) {
		makeObservable(this);

		this.cache = cache || new StoreCache();
		this._currentLayout = layoutNode;
		this.loadLayouts(layoutNode, this.cache);
		this.switchLayout(layoutNode);
	}
}

export const PresentationPopupStoreContext = createContext<PresentationPopupStore>(undefined);
export const usePresentationPopupStore = (): PresentationPopupStore => useContext(PresentationPopupStoreContext);